import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { If } from "react-conditional-components-renderer";
import FocusTrap from "focus-trap-react";

import { CloseFilledRectangleIcon } from "../AscIcons";
import AscPortal from "../AscPortal/AscPortal";
import styles from "./AcsDrawer.module.scss";

export interface AscDrawerProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  backdropClassName?: string;
  drawerClassName?: string;
  innerContentClassName?: string;
  headerClassName?: string;
}

export default function AscDrawer({
  visible,
  setVisible,
  children,
  drawerClassName,
  backdropClassName,
  innerContentClassName,
  header,
  footer,
  headerClassName,
}: AscDrawerProps) {
  const [isFocusTrapActive, setActivateFocusTrap] = useState(false);

  function closeDrawer() {
    setVisible(false);
    setActivateFocusTrap(false);
  }
  function stopPropagation(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e?.stopPropagation();
  }

  useEffect(() => {
    const trapActivationTimeOut = setTimeout(() => {
      if (visible) {
        setActivateFocusTrap(true);
      }
    }, 500);

    return () => {
      setActivateFocusTrap(false);
      clearTimeout(trapActivationTimeOut);
    };
  }, [visible]);

  return (
    <AscPortal>
      <FocusTrap
        active={isFocusTrapActive}
        focusTrapOptions={{
          delayInitialFocus: true,
          allowOutsideClick: true,
        }}
      >
        <div
          className={classNames(
            styles.ground,
            { [styles.visible]: visible },
            backdropClassName
          )}
          data-testid="asc-drawer-backdrop"
          onClick={closeDrawer}
        >
          <div
            className={classNames(styles.drawerContent, drawerClassName)}
            onClick={stopPropagation}
          >
            <If test={!!header}>
              <header className={classNames(styles.header, headerClassName)}>
                {header}
                <span
                  onClick={closeDrawer}
                  className={styles.closeIconWrapper}
                  data-testid="asc-drawer-close-icon-wrapper"
                  tabIndex={0}
                >
                  <CloseFilledRectangleIcon />
                </span>
              </header>
            </If>
            <div
              className={classNames(styles.innerContent, innerContentClassName)}
            >
              {children}
            </div>
            <If test={!!footer}>
              <footer className={styles.footer}>{footer}</footer>
            </If>
          </div>
        </div>
      </FocusTrap>
    </AscPortal>
  );
}
